import React from 'react';
import { Wallet, BadgeCheck, FileSignature, Send, HandCoins, Info, ListChecks, Receipt, Fuel, DollarSign, FileText, BookOpen } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const STEPS = [
  { icon: Wallet, title: 'Open your BETH wallet', text: 'Sign up with your name, email and SSN or EIN. Your BETH wallet is created for you right away.' },
  { icon: BadgeCheck, title: 'Subscribe as a Treasury Officer', text: 'Choose the $499/month Treasury Officer plan in Banking & Credit. It unlocks Mint Check and the credit line.' },
  { icon: FileSignature, title: 'Get approved and sign', text: 'Once your credit line is approved, sign the Subscriber Credit Agreement and Promissory Note online. Collateral documents can be added later.' },
  { icon: Send, title: 'Mint, sign and send a check', text: 'Tap Mint Check, enter the amount and the payee. The check is recorded on the Base network, and the payee is emailed a receipt. A 1% card fee applies to each check action.' },
  { icon: HandCoins, title: 'The payee claims it', text: "The payee opens the emailed link and claims the check to their own wallet, free. Claim links work for 30 days, and you can resend a fresh one." },
];

const PROCESS_CHECK_STEPS = [
  { title: 'Go to Mint Check', text: 'From your dashboard, click "Mint Check" in Banking & Credit. You must be a Treasury Officer subscriber to mint checks.' },
  { title: 'Enter amount and payee', text: 'Type the dollar amount (e.g., $1,000) and the payee\'s name and email. The equivalent BETH is calculated automatically.' },
  { title: 'Review and confirm', text: 'Check the fee breakdown (1% of check amount, min $0.50). Click "Mint Check" to record it on the Base blockchain.' },
  { title: 'Payee receives email', text: 'The payee gets an email with a claim link. They click it to accept the check into their own wallet (no fee for claiming).' },
  { title: 'Track your checks', text: 'View all your issued checks in "Issued Checks" with their status: Pending, Claimed, or Expired.' },
];

const BUYING_ETH_STEPS = [
  { title: 'Why ETH?', text: 'Gas fees on the Base network (where your checks are minted) are paid in ETH. You need a small ETH balance to cover these fees.' },
  { title: 'Buy via Stripe', text: 'In Banking & Credit, use the "Buy ETH" card. Enter the USD amount and pay with card. ETH is sent directly to your wallet.' },
  { title: 'Use MetaMask', text: 'Alternatively, buy ETH on an exchange (Coinbase, Kraken) and transfer to your linked wallet address shown in your dashboard.' },
  { title: 'Gas fees covered', text: 'For Treasury Officer subscribers, the platform treasury covers most gas fees. You only pay the 1% platform fee via card checkout.' },
];

const FEE_INFO = [
  { title: 'Treasury Officer Subscription', text: '$499.00/month. Unlocks check minting, credit lines, and priority support. Cancel anytime.' },
  { title: 'Check Minting Fee', text: '1% of the check amount (minimum $0.50). Paid via Stripe at checkout. Gas is covered by the platform treasury.' },
  { title: 'Check Claiming', text: 'FREE for payees. No fee to claim a check sent to you.' },
  { title: 'Credit Agreement Deploy', text: 'Platform fee + gas (varies by network). Fee-exempt for super admins.' },
  { title: 'ETH Purchases', text: 'Market price + small spread. No hidden fees.' },
];

const FORM_PURPOSES = [
  { title: 'Subscriber Credit Agreement', text: 'The legal contract between you and BETHCOIN Exchange. Sets your credit limit, term, APR, and repayment schedule. Required to mint checks.' },
  { title: 'Promissory Note', text: 'Your formal promise to repay the credit extended to you. Outlines interest rate, payment terms, and consequences of default.' },
  { title: 'Collateral Documents', text: 'Optional proof of assets (e.g., Certificate of Live Birth, Social Security Card). Improves your credit line and reduces restrictions.' },
  { title: 'IRS Form 8824', text: 'Used for like-kind exchanges under IRC §1031. Generated when you complete qualifying barter transactions with BETH.' },
  { title: 'Treasury Check Face', text: 'The digital check itself, showing amount, payee, date, crypto signature, and MICR line. Verifiable on-chain.' },
];

const Steps = ({ compact }) => (
  <ol className={`grid gap-4 ${compact ? '' : 'md:grid-cols-5'}`}>
    {STEPS.map((s, i) => (
      <li key={s.title} className="relative bg-slate-800/70 border border-slate-700 rounded-xl p-5 text-left" data-testid={`how-step-${i + 1}${compact ? '-dialog' : ''}`}>
        <div className="flex items-center gap-3 mb-3">
          <span className="w-8 h-8 rounded-full bg-emerald-500 text-slate-900 font-bold flex items-center justify-center text-sm">{i + 1}</span>
          <s.icon className="w-5 h-5 text-emerald-400" />
        </div>
        <h3 className="text-white font-semibold mb-1">{s.title}</h3>
        <p className="text-slate-400 text-sm leading-relaxed">{s.text}</p>
      </li>
    ))}
  </ol>
);

const colorMap = {
  emerald: 'bg-emerald-500',
  cyan: 'bg-cyan-500',
  purple: 'bg-purple-500',
  amber: 'bg-amber-500',
  blue: 'bg-blue-500',
};

const InfoList = ({ items, color = 'emerald' }) => (
  <ol className="grid gap-3">
    {items.map((item, i) => (
      <li key={item.title} className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 text-left">
        <div className="flex items-start gap-3">
          <span className={`w-6 h-6 rounded-full ${colorMap[color] || colorMap.emerald} text-slate-900 font-bold flex items-center justify-center text-xs shrink-0 mt-0.5`}>{i + 1}</span>
          <div>
            <h4 className="text-white font-semibold text-sm">{item.title}</h4>
            <p className="text-slate-400 text-sm leading-relaxed mt-1">{item.text}</p>
          </div>
        </div>
      </li>
    ))}
  </ol>
);

const Notice = () => (
  <p className="flex items-start gap-2 text-xs text-slate-400 leading-relaxed text-left" data-testid="how-it-works-notice">
    <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
    BETHCOIN is a private digital token and credit service. It is not a bank, is not FDIC insured, and is not affiliated with or endorsed by the U.S. Treasury or any government agency. A BETHCOIN Treasury Check is not a U.S. Government check. Credit is subject to approval, and crypto values can go down as well as up.
  </p>
);

export const HowItWorksSection = () => (
  <section id="how-it-works" className="py-20 px-4" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto">
      <div className="text-center mb-10">
        <p className="text-emerald-400 text-sm font-semibold tracking-widest uppercase mb-2">Instructions</p>
        <h2 className="text-3xl md:text-4xl font-bold text-white">How it works</h2>
      </div>
      <Steps />
      <div className="mt-8 max-w-4xl mx-auto"><Notice /></div>
    </div>
  </section>
);

export const HowItWorksButton = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button data-testid="how-it-works-btn" className="px-8 py-4 border-2 border-emerald-500/50 hover:border-emerald-400 text-emerald-300 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-colors">
        <ListChecks className="w-5 h-5" /> How it works
      </button>
    </DialogTrigger>
    <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-xl max-h-[85vh] overflow-y-auto" data-testid="how-it-works-dialog">
      <DialogHeader><DialogTitle className="text-white text-2xl">How it works</DialogTitle></DialogHeader>
      <Steps compact />
      <Notice />
    </DialogContent>
  </Dialog>
);

export const ProcessCheckButton = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button data-testid="process-check-btn" className="px-6 py-3 border border-cyan-500/50 hover:border-cyan-400 text-cyan-300 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
        <Receipt className="w-4 h-4" /> Process a Check
      </button>
    </DialogTrigger>
    <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="process-check-dialog">
      <DialogHeader><DialogTitle className="text-white text-xl">How to Process a Check</DialogTitle></DialogHeader>
      <InfoList items={PROCESS_CHECK_STEPS} color="cyan" />
    </DialogContent>
  </Dialog>
);

export const BuyingEthButton = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button data-testid="buying-eth-btn" className="px-6 py-3 border border-purple-500/50 hover:border-purple-400 text-purple-300 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
        <Fuel className="w-4 h-4" /> Buying ETH
      </button>
    </DialogTrigger>
    <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="buying-eth-dialog">
      <DialogHeader><DialogTitle className="text-white text-xl">Buying ETH (Gas Fees)</DialogTitle></DialogHeader>
      <InfoList items={BUYING_ETH_STEPS} color="purple" />
    </DialogContent>
  </Dialog>
);

export const FeesButton = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button data-testid="fees-btn" className="px-6 py-3 border border-amber-500/50 hover:border-amber-400 text-amber-300 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
        <DollarSign className="w-4 h-4" /> Fees
      </button>
    </DialogTrigger>
    <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="fees-dialog">
      <DialogHeader><DialogTitle className="text-white text-xl">Fees & Pricing</DialogTitle></DialogHeader>
      <InfoList items={FEE_INFO} color="amber" />
    </DialogContent>
  </Dialog>
);

export const FormPurposesButton = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button data-testid="form-purposes-btn" className="px-6 py-3 border border-blue-500/50 hover:border-blue-400 text-blue-300 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors">
        <FileText className="w-4 h-4" /> Purpose of Forms
      </button>
    </DialogTrigger>
    <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="form-purposes-dialog">
      <DialogHeader><DialogTitle className="text-white text-xl">Purpose of Forms</DialogTitle></DialogHeader>
      <InfoList items={FORM_PURPOSES} color="blue" />
    </DialogContent>
  </Dialog>
);

export const InstructionsBar = () => (
  <div className="flex flex-wrap gap-3 justify-center" data-testid="instructions-bar">
    <HowItWorksButton />
    <ProcessCheckButton />
    <BuyingEthButton />
    <FeesButton />
    <FormPurposesButton />
  </div>
);

// Compact button styles for the top bar
const compactBtnClass = "px-4 py-2 border rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors";

export const InstructionsBarCompact = () => (
  <div className="flex flex-wrap gap-2 justify-center" data-testid="instructions-bar-compact">
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="how-it-works-compact-btn" className={`${compactBtnClass} border-emerald-500/50 hover:border-emerald-400 text-emerald-300`}>
          <BookOpen className="w-3.5 h-3.5" /> How it works
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-xl max-h-[85vh] overflow-y-auto" data-testid="how-it-works-compact-dialog">
        <DialogHeader><DialogTitle className="text-white text-2xl">How it works</DialogTitle></DialogHeader>
        <Steps compact />
        <Notice />
      </DialogContent>
    </Dialog>
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="process-check-compact-btn" className={`${compactBtnClass} border-cyan-500/50 hover:border-cyan-400 text-cyan-300`}>
          <Receipt className="w-3.5 h-3.5" /> Process a Check
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="process-check-compact-dialog">
        <DialogHeader><DialogTitle className="text-white text-xl">How to Process a Check</DialogTitle></DialogHeader>
        <InfoList items={PROCESS_CHECK_STEPS} color="cyan" />
      </DialogContent>
    </Dialog>
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="buying-eth-compact-btn" className={`${compactBtnClass} border-purple-500/50 hover:border-purple-400 text-purple-300`}>
          <Fuel className="w-3.5 h-3.5" /> Buying ETH
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="buying-eth-compact-dialog">
        <DialogHeader><DialogTitle className="text-white text-xl">Buying ETH (Gas Fees)</DialogTitle></DialogHeader>
        <InfoList items={BUYING_ETH_STEPS} color="purple" />
      </DialogContent>
    </Dialog>
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="fees-compact-btn" className={`${compactBtnClass} border-amber-500/50 hover:border-amber-400 text-amber-300`}>
          <DollarSign className="w-3.5 h-3.5" /> Fees
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="fees-compact-dialog">
        <DialogHeader><DialogTitle className="text-white text-xl">Fees & Pricing</DialogTitle></DialogHeader>
        <InfoList items={FEE_INFO} color="amber" />
      </DialogContent>
    </Dialog>
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="form-purposes-compact-btn" className={`${compactBtnClass} border-blue-500/50 hover:border-blue-400 text-blue-300`}>
          <FileText className="w-3.5 h-3.5" /> Purpose of Forms
        </button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] rounded-xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="form-purposes-compact-dialog">
        <DialogHeader><DialogTitle className="text-white text-xl">Purpose of Forms</DialogTitle></DialogHeader>
        <InfoList items={FORM_PURPOSES} color="blue" />
      </DialogContent>
    </Dialog>
  </div>
);
